<template>
  <div>
    <Loader v-if="isLoading" style="position: absolute"></Loader>
    <div v-if="customer" class="ui form">
      <h1 class="org-settings-heading">Organization Settings</h1>
      <SrpTabs v-model:activeTab="activeTab" tabsFillWidth>
        <template #DestinationInfo-header="{ active }">
          <TabHeader :is-active="active" title="Destination Info" description="Basic info, site, and handles" icon="map-pin_1-5" />
        </template>
        <template #DestinationInfo>
          <div class="ui stackable grid">
            <div class="nine wide column">
              <div class="field">
                <label class="cms-section-title">Name</label>
                <p class="cms">The destination name you would like displayed</p>
                <input type="text" v-model="customer.title" />
              </div>

              <div v-if="!isHotel" class="field">
                <label class="cms-section-title">Description</label>
                <p class="cms">A short description of your destination</p>
                <textarea v-model="customer.description" autocapitalize="sentences"></textarea>
              </div>

              <div v-if="!isHotel" class="field">
                <label class="cms-section-title">Website</label>
                <p class="cms">The url of your website</p>
                <input type="text" v-model="customer.website" />
              </div>
              <div v-if="!isHotel" class="field">
                <label class="cms-section-title">Website Display Text</label>
                <p class="cms">(Optional) If you want to show this text instead of your website url</p>
                <input type="text" v-model="customer.websiteText" />
              </div>

              <div class="field">
                <label class="cms-section-title expandable-title">Social Media Handles</label>
                <p class="cms" style="margin-top: 0">Shown on your destination page and helps creators tag your handle during a creator visit.</p>
                <SocialMediaInputs :socialFields="customer" />
              </div>
            </div>

            <div class="four wide column right floated">
              <div class="ui form" style="margin-bottom: 22px">
                <div class="field">
                  <br />
                  <input @change="uploadLogo()" class="inputfile" type="file" id="logoImageInput" accept="image/*" />
                  <input type="text" v-model="customer.icon" style="display: none" />
                  <img :src="`${contentBaseUri}/cms/images/orgs/${customer.icon}`" class="ui image profile-image ui raised segment" />

                  <br />
                  <label for="logoImageInput">
                    <SrpButton size="small" fill="outlined" style="width: 100%">
                      <i class="ui upload icon"></i>
                      Upload Logo
                    </SrpButton>
                  </label>
                </div>
              </div>

              <div style="margin-bottom: 14px; border-bottom: 1px rgba(0, 0, 0, 0.1) solid"></div>

              <div style="margin-bottom: 17px; display: flex; align-items: center; color: rgba(91, 91, 91, 1); font-family: sans-serif">
                <GlobalAccentColorPicker style="margin: 0 10px 4px 0; z-index: 10" dropdownPosition="top" @change="newColorHex => (customer.accentColor = newColorHex)" />
              </div>
            </div>
          </div>
        </template>
        <template #CreatorVisits-header="{ active }">
          <TabHeader :is-active="active" title="Creator Visits" description="Guidance for all creator visits" icon="briefcase_1-5" />
        </template>
        <template #CreatorVisits>
          <div v-if="settings" class="creator-visits-tab">
            <div class="field">
              <label class="cms-section-title">Creator Visit Guidance</label>
              <p class="cms">This guidance will apply across every creator visit and will be visible to creators while applying and after accepting a creator visit.</p>
              <textarea
                v-model="settings.creatorDirection"
                autocapitalize="sentences"
                rows="4"
                style="height: 120px"
                placeholder="Ex. Stay within the county boundaries.
Or, stop by the visitor center to say hi. "
                :maxlength="CREATOR_DIRECTION_MAX_LENGTH"
              ></textarea>
              <p v-if="(settings.creatorDirection?.length ?? 0) >= CREATOR_DIRECTION_MAX_LENGTH" class="creator-direction-limit-warning">
                {{ CREATOR_DIRECTION_MAX_LENGTH.toLocaleString() }} character limit
              </p>
            </div>

            <div ref="boundaryEditorSection" class="field">
              <label class="cms-section-title expandable-title">Boundary</label>
              <p class="cms" style="margin-top: 0">Show creators the boundary of your destination.</p>
              <CustomerBoundaryEditor
                :key="boundaryEditorKey"
                v-model:boundarySelectMode="settings.boundarySelectMode"
                v-model:withinMiles="settings.withinMiles"
                v-model:boundaryGeoJson="settings.boundaryGeoJson"
                v-model:boundaryAreaInMiles="settings.boundaryAreaInMiles"
                :latitude="customer.latitude"
                :longitude="customer.longitude"
                :validationMessage="boundaryValidationMessage"
                @modeChanged="boundaryModeChanged"
                @markerMoved="markerManuallyMoved"
              />
            </div>
          </div>
        </template>
      </SrpTabs>

      <div class="community-page-admin__sticky-footer">
        <div class="community-page-admin__sticky-footer-actions">
          <SrpButton @click="save" class="community-page-admin__sticky-footer-button">Save</SrpButton>
          <SrpButton lang="en" hreflang="en" fill="outlined" @click="saveAndPreview">
            {{ activeTab === "DestinationInfo" ? "Destination Page" : "Creator Visits" }}
            <template #iconRight><IconEmbedded name="arrow-right_2" :size="18" /></template>
          </SrpButton>
          <span v-if="saveText" class="community-page-admin__sticky-footer-status">{{ saveText }}</span>
          <span v-if="imageUploading" class="community-page-admin__sticky-footer-status">Uploading...</span>
          <div v-if="errors.length" class="community-page-admin__sticky-footer-errors">
            <ul>
              <li v-for="error in errors" :key="error">
                <b>{{ error }}</b>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="community-page-admin__super-panel-wrap">
        <SuperUserActionPanel class="community-page-admin__super-panel" :class="{ 'community-page-admin__super-panel--expanded': isSuperuser && showCensusBoundaryPicker }">
          <div class="community-page-admin__super-actions">
            <router-link :to="{ name: 'EditCustomer', params: { id: customer.id } }" target="_blank">
              <SrpButton color="gray" fill="outlined" size="tiny" style="opacity: 0.5">Backend edit</SrpButton>
            </router-link>
            <SrpButton
              v-if="isSuperuser && settings"
              color="gray"
              fill="outlined"
              size="tiny"
              style="opacity: 0.5"
              :aria-expanded="showCensusBoundaryPicker"
              aria-controls="destination-census-boundary"
              @click="showCensusBoundaryPicker = !showCensusBoundaryPicker"
            >
              Set Boundary
            </SrpButton>
          </div>
          <CensusBoundaryPicker
            v-if="isSuperuser && settings && showCensusBoundaryPicker"
            id="destination-census-boundary"
            :state="customer.state"
            :latitude="customer.latitude"
            :longitude="customer.longitude"
            :suggestionNames="censusSuggestionNames"
            :countyName="null"
            @applyBoundary="applyCensusBoundary"
            @manualBoundary="startManualBoundary"
          />
        </SuperUserActionPanel>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import axios from "axios";
import Loader from "@components/Loader/Loader.vue";
import { Page, CustomerAdminEditV2Response, CustomerSettingsClientContract } from "@contracts/pages";
import CustomerBoundaryEditor from "./CustomerBoundaryEditor.vue";
import CensusBoundaryPicker from "@views/admin/components/CensusBoundaryPicker.vue";
import FileUtils from "@logic/FileUtils";
import SocialMediaInputs from "@components/SocialMediaInputs.vue";
import SocialFieldValidator from "@logic/SocialFieldValidator";
import { inject } from "vue";
import SrpModal from "@components/ui/SrpModal.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import { useHead } from "@unhead/vue";
import AdminContext from "@logic/AdminContext";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import SrpDetailsSummary from "@components/ui/SrpDetailsSummary.vue";
import GlobalAccentColorPicker from "@components/GlobalAccentColorPicker.vue";
import InfoIconWithTooltip from "@components/ui/InfoIconWithTooltip.vue";
import { MetricSender } from "@/helpers/MetricSender";
import SuperUserActionPanel from "@components/SuperUserActionPanel.vue";
import SrpTabs from "@components/ui/SrpTabs.vue";
import TabHeader from "@views/Creator/CollabOpportunity/CollabOpportunityV2/Tabs/TabHeader.vue";
import { useUserProfileStore } from "@stores/userProfileStore";

const CREATOR_DIRECTION_MAX_LENGTH = 2000;
const HASH_TO_TAB: Record<string, "DestinationInfo" | "CreatorVisits"> = { info: "DestinationInfo", visits: "CreatorVisits" };
const TAB_TO_HASH: Record<"DestinationInfo" | "CreatorVisits", string> = { DestinationInfo: "info", CreatorVisits: "visits" };

export default defineComponent({
  name: "CommunityPageAdmin",

  components: {
    InfoIconWithTooltip,
    GlobalAccentColorPicker,
    SrpButton,
    SrpModal,
    Loader,
    CustomerBoundaryEditor,
    CensusBoundaryPicker,
    SocialMediaInputs,
    IconEmbedded,
    SrpDetailsSummary,
    SuperUserActionPanel,
    SrpTabs,
    TabHeader,
  },

  setup() {
    return { userProfileStore: useUserProfileStore() };
  },

  data() {
    return {
      CREATOR_DIRECTION_MAX_LENGTH,
      globalLog: inject("globalLog") as any,
      globalRemoteLogger: inject("globalRemoteLogger") as any,

      activeTab: "DestinationInfo" as "DestinationInfo" | "CreatorVisits",
      title: "Organization Settings",
      customer: null as Page | null,
      settings: null as CustomerSettingsClientContract | null,
      isHotel: false,
      isLoading: true,
      showUnsavedChangesModal: false,
      saveText: null as string | null,
      errors: [] as Array<string>,
      boundaryValidationMessage: null,
      boundaryEditorKey: 0,
      showCensusBoundaryPicker: false,
      imageUploading: false,
      // @ts-ignore
      contentBaseUri: globalThis.Bootstrap.Config.contentCdnBaseUri,
    };
  },

  computed: {
    isSuperuser(): boolean {
      return this.userProfileStore.getActingUserProfile?.isSuperuser === true;
    },
    censusSuggestionNames(): string[] {
      const name = this.customer?.title?.trim();
      return name ? [name] : [];
    },
  },

  watch: {
    activeTab(newTab) {
      const hash = TAB_TO_HASH[newTab];
      const currentHash = this.$route.hash?.replace(/^#/, "") ?? "";
      if (currentHash !== hash) {
        this.$router.replace({
          name: this.$route.name ?? "CommunityPageAdmin",
          params: this.$route.params,
          query: this.$route.query,
          hash: `#${hash}`,
        });
      }
    },
    "$route.hash"(newHash) {
      const hashKey = newHash?.replace(/^#/, "") ?? "";
      const tab = HASH_TO_TAB[hashKey];
      if (tab && this.activeTab !== tab) {
        this.activeTab = tab;
      }
    },
  },

  async mounted() {
    useHead({ title: () => this.title ?? "" });

    const hashKey = this.$route.hash?.replace(/^#/, "") ?? "";
    const tab = HASH_TO_TAB[hashKey];
    if (tab) {
      this.activeTab = tab;
    }

    const id = this.$route.params.pageId as string;
    await AdminContext.setOrgInContextFromCustomerId(id);
    // @ts-ignore
    await this.loadData(id);
    AdminContext.setOrgInContextFromCustomer(this.customer);

    MetricSender.sendMetric("Cust-ViewDestSettings", null);
  },

  methods: {
    applyCensusBoundary(geoJson: string, areaSquareMiles: number) {
      this.settings.boundarySelectMode = "draw";
      this.settings.boundaryGeoJson = geoJson;
      this.settings.boundaryAreaInMiles = areaSquareMiles;
      this.boundaryModeChanged();
      this.boundaryEditorKey++;
      this.saveText = "Boundary updated. Click Save to save your changes.";
    },
    async startManualBoundary() {
      this.settings.boundarySelectMode = "draw";
      this.settings.boundaryGeoJson = null;
      this.settings.boundaryAreaInMiles = null;
      this.boundaryModeChanged();
      this.boundaryEditorKey++;
      this.saveText = null;
      this.activeTab = "CreatorVisits";
      await this.$nextTick();
      (this.$refs.boundaryEditorSection as HTMLElement | undefined)?.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    async loadData(identifier: string) {
      const uri = `${import.meta.env.VITE_API_URL}/pages/${identifier}/admin-edit/v2`;
      const { data } = await axios.get<CustomerAdminEditV2Response>(uri);
      this.customer = data.customer;
      this.settings = data.settings;
      this.isHotel = this.customer.type === "Hotel";
      this.isLoading = false;
      this.title += ` - ${this.customer.title}`;
    },
    async save(): Promise<boolean> {
      this.prepForSave();
      this.saveText = "";
      // Validate
      this.errors = [];
      this.boundaryValidationMessage = null;
      var socialFieldValidation = SocialFieldValidator.validateSocialFields(this.customer);
      if (!this.customer.title) {
        this.errors.push("Name is required");
        return false;
      }
      if (socialFieldValidation[0] === false) {
        this.errors.push(...socialFieldValidation[1]);
        return false;
      } else if (!this.settings.boundarySelectMode || this.settings.boundarySelectMode.trim().length === 0) {
        let message = "Select a boundary mode";
        this.errors.push(message);
        this.boundaryValidationMessage = message;
        return false;
      } else if (this.settings.boundarySelectMode === "draw" && (!this.settings.boundaryGeoJson || this.settings.boundaryGeoJson.trim().length === 0)) {
        let message = "Click the map to draw your boundary";
        this.errors.push(message);
        this.boundaryValidationMessage = message;
        return false;
      } else {
        // Save it!
        this.globalLog.info("Saving page!");
        const uri = `${import.meta.env.VITE_API_URL}/pages/${this.customer.id}/admin-edit/v2`;
        this.saveText = "Saving...";
        const { data } = await axios.put<CustomerAdminEditV2Response>(uri, { customer: this.customer, settings: this.settings });
        this.customer = data.customer;
        this.settings = data.settings;
        this.saveText = "Saved";
        return true;
      }
    },
    boundaryModeChanged() {
      this.boundaryValidationMessage = null;
      this.errors = [];
    },
    prepForSave() {
      // Ensure the website starts with http
      if (this.customer.website && !this.customer.website.startsWith("http")) {
        this.customer.website = "https://" + this.customer.website;
      }
      if (!this.customer.websiteText || this.customer.websiteText.trim().length === 0) {
        this.customer.websiteText = null;
      }
      SocialFieldValidator.fixSocialFields(this.customer);
    },
    markerManuallyMoved(lat: number, lng: number) {
      this.customer.latitude = lat;
      this.customer.longitude = lng;
    },
    async saveAndPreview() {
      if (await this.save()) {
        if (this.activeTab === "DestinationInfo") {
          this.$router.push({ name: "Pages", params: { pageId: this.customer.uniqueName } });
        } else {
          this.$router.push({ name: "PaidCollabCommunityAdmin", params: { communityId: this.customer.id } });
        }
      }
    },
    getGoogleMapLatLngLink(): string {
      return `https://www.google.com/maps/search/?api=1&query=${this.customer.latitude},${this.customer.longitude}`;
    },
    async uploadLogo() {
      // Note: Similar code in Profile.vue
      this.imageUploading = true;
      var uploadInput: any = document.getElementById("logoImageInput");
      if (uploadInput.files.length > 0) {
        var file = uploadInput.files[0];
        var error = FileUtils.isFileSupportedForUpload(file.name, file.type, file.size);
        if (error?.length > 0) {
          this.saveText = error;
        } else {
          var formData = new FormData();
          formData.append("file", file);
          try {
            var result = await axios.post(`${import.meta.env.VITE_API_URL}/images?type=CustomerIcon&flow=logo&ownerId=${this.customer.id}`, formData);
            this.saveText = null;
            this.customer.icon = result.data[0];
          } catch (error) {
            if (error.response?.status === 400) this.saveText = error.response.data;
            else this.saveText = "Error uploading image, please contact support@shrpa.com";
          }
        }

        // Note: Ideally should set the nav icon on partnerInfo in App.vue also...

        this.imageUploading = false;
      }
    },
  },
});
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";
@import "@/scss/variables.scss";

.org-settings-heading {
  margin-bottom: 25px;
}

.expandable-title {
  margin-top: 5px !important;
  margin-left: 3px !important;
}

.creator-visits-tab {
  min-height: 200px;
}

.creator-direction-limit-warning {
  font-size: 14px;
  margin-top: 4px;
  color: #945347;
  font-weight: 500;
}

.community-page-admin__super-panel-wrap {
  margin-top: 50px;
  display: flex;
  justify-content: flex-end;
  width: 100%;
}

.community-page-admin__super-panel {
  min-width: 0;
  max-width: 100%;

  &--expanded {
    width: 100%;
  }
}

.community-page-admin__super-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.community-page-admin__super-panel :deep(.census-boundary-picker__points label) {
  white-space: nowrap;
}

.community-page-admin__super-panel :deep(.census-boundary-picker__points input[type="number"]) {
  width: 68px;
}

@media (max-width: 650px) {
  .community-page-admin__super-panel :deep(.census-boundary-picker__points input[type="number"]) {
    width: 60px;
  }
}

// Sticky footer (patterned after StepCalendarPosts manage-buttons-floating-panel)
.community-page-admin__sticky-footer {
  position: sticky;
  inset: auto auto 0 0;
  margin-top: 25px;
  margin-bottom: 25px;
  padding-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  box-shadow: 0 -11px 16px 14px rgba(255, 255, 255, 1);
  background: rgba(255, 255, 255, 1);
  z-index: 10;
}

.community-page-admin__sticky-footer-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  width: 100%;
}

.community-page-admin__sticky-footer-button {
  margin-right: 0;
}

.community-page-admin__sticky-footer-status {
  font-weight: bold;
  margin-left: 8px;
}

.community-page-admin__sticky-footer-errors {
  width: 100%;
  color: firebrick;
  margin-top: 8px;

  ul {
    margin: 0;
    padding-left: 1.25rem;
  }
}

@media (max-width: $mobile-max-width) {
  .community-page-admin__sticky-footer {
    box-shadow: 0 -5px 20px 8px rgba(255, 255, 255, 1);
    margin-top: 20px;
    margin-bottom: 20px;
    padding: 12px;
  }

  .community-page-admin__sticky-footer-actions {
    gap: 10px;
  }
}
</style>
